import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../lib/api';
import { redirectToCorrectTenant, detectTenant, TENANT_TYPES } from '../config/tenantConfig';

const DEMO_TOKEN_KEY = 'demo-auth-token';
const DEMO_USERS_KEY = 'demo-auth-users';
const DEMO_CURRENT_USER_KEY = 'demo-auth-current-user';

const sanitizeUser = (user) => {
  if (!user) return null;
  const { password, ...rest } = user;
  return rest;
};

const applyTenantCategory = (user) => {
  if (!user) return user;
  // Don't override user category - respect what's in the database
  // Only add tenant info for tracking purposes
  const currentTenant = detectTenant();
  return { ...user, tenant: currentTenant };
};

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyActivationTokenFromUrl = () => {
    if (typeof window === 'undefined' || !window.location?.hash) {
      // still allow query param flow
    }
    
    // Clean up invalid token=undefined from URL
    if (window.location.search?.includes('token=undefined')) {
      const cleanUrl = window.location.href.replace(/[?&]token=undefined/g, '').replace(/\?$/, '');
      if (window.history?.replaceState) {
        window.history.replaceState(null, document.title, cleanUrl);
      }
      return false;
    }
    
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const searchParams = new URLSearchParams(window.location.search || '');
    const activationToken = hashParams.get('token') || searchParams.get('token');
    const activationFlag = hashParams.get('activated') || searchParams.get('activated');

    if (!activationToken || activationFlag !== '1') {
      return false;
    }

    localStorage.setItem('token', activationToken);
    localStorage.setItem('lead-invite-activated', String(Date.now()));
    localStorage.setItem('selectedWhiteLabelProfile', 'halitrade');
    sessionStorage.setItem('multiTenant.currentTenant', 'halitrade');
    sessionStorage.setItem('just_logged_in', '1');
    sessionStorage.setItem('just_navigated', '1');

    localStorage.removeItem(DEMO_TOKEN_KEY);
    localStorage.removeItem(DEMO_USERS_KEY);
    localStorage.removeItem(DEMO_CURRENT_USER_KEY);

    const cleanUrl = window.location.href.split('#')[0].split('?')[0];
    if (window.history?.replaceState) {
      window.history.replaceState(null, document.title, cleanUrl);
    } else {
      window.location.hash = '';
    }

    return true;
  };

  // Vérifie l'état d'authentification à partir du token stocké
  const checkAuthStatus = async () => {
    applyActivationTokenFromUrl();

    // SECURITY: Removed URL email parameter authentication (was an auth bypass vector)
    // Cross-domain auth now uses proper JWT tokens only

    const token = localStorage.getItem('token');

    // Check if we just completed login or navigation - skip redirect to avoid double redirect
    const justLoggedIn = sessionStorage.getItem('just_logged_in');
    const justNavigated = sessionStorage.getItem('just_navigated');
    // Remove flags but keep the value for later check
    if (justLoggedIn) {
      sessionStorage.removeItem('just_logged_in');
    }
    if (justNavigated) {
      sessionStorage.removeItem('just_navigated');
    }
    const skipRedirect = Boolean(justLoggedIn || justNavigated);

    if (token) {
      if (token === DEMO_TOKEN_KEY) {
        // Legacy local demo token is no longer supported; require backend auth.
        localStorage.removeItem('token');
      }

      try {
        const response = await api.get('/api/check-auth');
        const serverUser = applyTenantCategory(sanitizeUser(response.data.user));
        setUser(serverUser);
        setCompany(serverUser?.company || null);
        setIsAuthenticated(true);
        // Debug only: console.log('User authenticated:', response.data.user);
      } catch (err) {
        // Only kill the token if the server explicitly says it is invalid
        // (401 / 403). On network errors, 404, 5xx, etc. keep the token so
        // that the user isn't randomly logged out by a transient blip.
        const status = err?.response?.status;
        const isAuthFailure = status === 401 || status === 403;
        console.error('Failed to authenticate token', { status, isAuthFailure, msg: err?.message });
        if (isAuthFailure) {
          setIsAuthenticated(false);
          setUser(null);
          localStorage.removeItem('token');
        }
        // For non-auth errors we leave the token in place; the next request
        // will retry, and api.js's refresh interceptor handles real 401s.
      }
    }
    setLoading(false);
  };

  // Exécute la vérification d'authentification au chargement du composant
  useEffect(() => {
    checkAuthStatus();
    const onLogout = () => {
      // Don't auto-logout if user just logged in (prevents race condition)
      const justLoggedIn = sessionStorage.getItem('just_logged_in');
      if (justLoggedIn) {
        console.log('[AuthContext] Ignoring auth:loggedOut event - user just logged in');
        return;
      }
      logout();
    };
    window.addEventListener('auth:loggedOut', onLogout);
    return () => window.removeEventListener('auth:loggedOut', onLogout);
  }, []);

  // Fonction de connexion
  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      // Clear stale company data BEFORE logging in to prevent 403 errors
      localStorage.removeItem('companyId');
      localStorage.removeItem('selectedTradePartnerId');

      const response = await api.post('/api/login', { email, password });
      // Defensive: refuse to store a missing/empty token. Previously we
      // unconditionally setItem('token', undefined) which writes the literal
      // string 'undefined' and caused every subsequent API call to send a
      // bogus Authorization header and fail.
      const tk = response?.data?.token;
      if (typeof tk !== 'string' || tk.length < 10) {
        console.error('[AuthContext] Login response missing valid token', {
          hasToken: !!tk, type: typeof tk,
          responseKeys: response?.data ? Object.keys(response.data) : null,
        });
        throw new Error('Login response missing token');
      }
      localStorage.setItem('token', tk);
      if (response.data.refreshToken) {
        localStorage.setItem('refresh_token', response.data.refreshToken);
      }

      let profile = null;
      try {
        const me = await api.get('/api/check-auth');
        profile = applyTenantCategory(sanitizeUser(me.data.user));
      } catch (fetchErr) {
        console.warn('Login succeeded but failed to fetch user profile');
        profile = applyTenantCategory(sanitizeUser({ email }));
      }

      // Set all authentication state synchronously before returning
      setUser(profile);
      setCompany(profile?.company || null);
      setIsAuthenticated(true);
      setLoading(false);

      // Sync companyId from DB to localStorage (if user has a company)
      if (profile?.company_id) {
        localStorage.setItem('companyId', String(profile.company_id));
      }

      // Set just_logged_in flag to prevent ProtectedRoute from redirecting
      // during the async state propagation
      sessionStorage.setItem('just_logged_in', '1');
      sessionStorage.setItem('just_navigated', '1');

      console.log('User logged in successfully', profile);

      // Don't redirect on login - let the caller handle navigation
      // This prevents the window.location.href issue that causes login loops

      return { success: true, user: profile };
    } catch (err) {
      const status = err.response?.status;
      const responseMessage = err.response?.data?.message;
      const baseURL = err.config?.baseURL || '';
      const endpoint = err.config?.url || '/api/login';
      const networkError = err.code === 'ERR_NETWORK' || (!err.response && err.request);

      let message = '';
      if (networkError) {
        message = 'Unable to reach the authentication service. Please check your connection or contact support.';
      } else if (responseMessage) {
        message = responseMessage;
      } else if (status) {
        message = `Login failed with status ${status}.`;
      } else if (err.message) {
        message = err.message;
      } else {
        message = 'Login failed. Please check your credentials.';
      }

      if (process.env.NODE_ENV !== 'production') {
        const detailBits = [];
        if (status) detailBits.push(`status ${status}`);
        if (baseURL) detailBits.push(`API ${baseURL}`);
        if (detailBits.length) {
          message = `${message} (${detailBits.join(', ')})`;
        }
      }

      try {
        console.error('[Auth] Login error', {
          status,
          baseURL,
          endpoint,
          code: err.code,
          message: err.message,
          response: err.response?.data || null,
        });
      } catch (_) {
        /* console might be unavailable */
      }

      setError(message);
      setIsAuthenticated(false);
      setLoading(false);
      throw new Error(message);
    }
  };

  const loginDemoUser = async (demoUser) => {
    setLoading(true);
    setError(null);
    try {
      if (!demoUser?.email) {
        throw new Error('Invalid demo user.');
      }

      // Clear stale company data
      localStorage.removeItem('companyId');
      localStorage.removeItem('selectedTradePartnerId');

      // Passwordless demo login — backend verifies password internally
      const response = await api.post('/api/demo-login', { email: demoUser.email });
      localStorage.setItem('token', response.data.token);
      if (response.data.refreshToken) {
        localStorage.setItem('refresh_token', response.data.refreshToken);
      }

      let profile = null;
      try {
        const me = await api.get('/api/check-auth');
        profile = applyTenantCategory(sanitizeUser(me.data.user));
      } catch (fetchErr) {
        console.warn('Demo login succeeded but failed to fetch user profile');
        profile = applyTenantCategory(sanitizeUser({ email: demoUser.email }));
      }

      setUser(profile);
      setCompany(profile?.company || null);
      setIsAuthenticated(true);
      setLoading(false);

      if (profile?.company_id) {
        localStorage.setItem('companyId', String(profile.company_id));
      }

      sessionStorage.setItem('just_logged_in', '1');
      sessionStorage.setItem('just_navigated', '1');

      return { success: true, user: profile };
    } catch (err) {
      const message = err.response?.data?.message || err?.message || 'Demo login failed.';
      setError(message);
      setIsAuthenticated(false);
      setLoading(false);
      throw new Error(message);
    }
  };

  // Fonction d'inscription
  const signup = async (formData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/api/signup', formData);
      const sanitized = applyTenantCategory(sanitizeUser(response.data.user));
      setUser(sanitized);
      setCompany(response.data.company || null);
      setIsAuthenticated(true);
      // Skip auto-redirect - let the auth module handle navigation
      // This prevents subdomain redirects that may not exist (e.g., halifund.halisoft.biz)
      // ModernAuthModule.jsx handles the correct route navigation after signup
      localStorage.setItem('token', response.data.token);
      if (response.data.refreshToken) {
        localStorage.setItem('refresh_token', response.data.refreshToken);
      }
      return { ...response.data, user: sanitized };
    } catch (err) {
      setError(err.response?.data?.message || 'Signup failed. Please check your information.');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir les données utilisateur
  const refreshUser = async () => {
    try {
      const response = await api.get('/api/check-auth');
      const serverUser = applyTenantCategory(sanitizeUser(response.data.user));
      setUser(serverUser);
      setCompany(serverUser?.company || null);
      return serverUser;
    } catch (err) {
      console.error('Failed to refresh user', err);
      return null;
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    console.log('[AuthContext] Logging out user:', user?.email, 'category:', user?.category);
    setUser(null);
    setIsAuthenticated(false);
    setCompany(null);

    // Clear ALL auth-related localStorage items
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('halisoft_token');
    localStorage.removeItem('halisoft_refresh_token');
    localStorage.removeItem(DEMO_TOKEN_KEY);
    localStorage.removeItem(DEMO_CURRENT_USER_KEY);
    localStorage.removeItem(DEMO_USERS_KEY);

    // Clear cross-domain auth email keys
    localStorage.removeItem('halisoft_email');
    localStorage.removeItem('admin_email');

    // Clear company-related localStorage to prevent stale data on next login
    localStorage.removeItem('companyId');
    localStorage.removeItem('selectedTradePartnerId');

    // Clear chat session data to prevent 403 errors on re-login
    localStorage.removeItem('liveChatSessionId');
    localStorage.removeItem('liveChatLastReadMessageId');

    console.log('[AuthContext] Logout complete - all auth data cleared');
  };

  // Fonction pour effacer les erreurs
  const clearError = () => setError(null);

  // Fournit les états et fonctions à utiliser dans les composants enfants
  return (
    <AuthContext.Provider value={{ user, company, isAuthenticated, login, loginDemoUser, signup, logout, refreshUser, loading, error, clearError }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
